<template>
    <div class="global-error-handler">
        <!-- DEV MODAL -->
        <e-modal v-if="isDev" v-model="modalActive" class="tw:w-screen tw:sm:w-[1024px]" has-modal-card trap-focus>
            <div class="modal-card tw:w-screen tw:sm:w-full!">
                <header class="modal-card-head">
                    <p class="modal-card-title">
                        <span>Error Details </span>
                        <span v-if="errors.length > 1">({{ activeErrorIndex + 1 }} / {{ errors.length }})</span>
                    </p>
                    <button class="delete" aria-label="close" @click="closeModal" />
                </header>

                <section v-if="currentError" class="modal-card-body">
                    <!-- Basic error info -->
                    <h4><strong>Message:</strong> {{ currentError.err?.message }}</h4>
                    <p><strong>Component:</strong> {{ currentError.instance?.$options.name || 'N/A' }}</p>
                    <p><strong>Vue Info:</strong> {{ currentError.info }}</p>
                    <p><strong>Occurred At:</strong> {{ currentError.date }}</p>

                    <p v-if="isAxiosError(currentError.err)" class="mt-4">
                        <e-button type="is-danger is-outlined" size="is-small" @click="openVisualException">
                            <strong>Laravel Exception</strong>
                        </e-button>
                    </p>

                    <hr />

                    <!-- If it's an Axios error, we can show request/response details. -->
                    <div v-if="isAxiosError(currentError.err)">
                        <e-collapse v-model="requestTab" aria-id="contentIdForA11y2" class="panel" animation="slide">
                            <template #trigger>
                                <div
                                    class="panel-heading"
                                    role="button"
                                    aria-controls="contentIdForA11y2"
                                    :aria-expanded="requestTab"
                                >
                                    <strong>
                                        {{ currentError.err.config.url }}
                                        ({{ currentError.err.response?.status || 'N/A' }})
                                    </strong>
                                </div>
                            </template>

                            <e-tabs size="is-small" class="block">
                                <e-tab-item label="Headers" icon="file-upload-alt">
                                    <pre>{{ formatted('config.headers') }}</pre>
                                </e-tab-item>
                                <e-tab-item label="Payload" icon="arrow">
                                    <pre>{{ formatted('config.data') }}</pre>
                                </e-tab-item>
                                <e-tab-item label="Response Headers" icon="file-download-alt">
                                    <pre>{{ formatted('response.headers') }}</pre>
                                </e-tab-item>
                                <e-tab-item label="Response Data" icon="brackets-curly">
                                    <pre>{{ formatted('response.data') }}</pre>
                                </e-tab-item>
                                <e-tab-item
                                    v-if="currentError.err.requestStack"
                                    label="Trace"
                                    icon="exclamation-octagon"
                                >
                                    <pre>{{ currentError.err.requestStack }}</pre>
                                </e-tab-item>
                            </e-tabs>
                        </e-collapse>
                    </div>

                    <hr />

                    <!-- Stack trace, if available -->
                    <div v-if="currentError.err?.stack">
                        <p><strong>Stack Trace:</strong></p>
                        <pre>{{ currentError.err.stack }}</pre>
                    </div>
                </section>

                <footer class="modal-card-foot">
                    <div class="buttons">
                        <button class="button" :disabled="activeErrorIndex === 0" @click="prevError">Previous</button>
                        <button class="button" :disabled="activeErrorIndex === errors.length - 1" @click="nextError">
                            Next
                        </button>

                        <button class="button is-danger" @click="clearAllErrors">Clear Errors</button>
                        <button class="button is-primary" @click="closeModal">Close</button>
                    </div>
                </footer>
            </div>
        </e-modal>

        <!-- PRODUCTION MODAL -->
        <e-modal v-else v-model="modalActive" class="tw:w-screen tw:sm:w-[1024px]" has-modal-card trap-focus>
            <div class="modal-card" style="width: 1024px">
                <header class="modal-card-head">
                    <p class="modal-card-title">Report a problem</p>
                    <button class="delete" aria-label="close" @click="closeModal" />
                </header>

                <section class="modal-card-body">
                    <!--user-menu v-if="authUser" :user="authUser" :show-dropdown="false" class="mb-4" /-->

                    <!--b-field label="Priority" required>
                        <e-select v-model="productionTicketForm.priority" required>
                            <option value="1">Low</option>
                            <option value="2">Medium</option>
                            <option value="3">High</option>
                            <option value="4">Urgent</option>
                        </e-select>
                    </e-field-->

                    <strong>Error: </strong>An error has occurred.<!--span>{{ currentError?.err?.message || 'No message available' }}</span-->
                    <hr />

                    <e-field label="Briefly explain what happened or what is not working:" required>
                        <rich-text-editor
                            v-model="productionTicketForm.description"
                            type="textarea"
                            placeholder="What were you doing when the error occurred?"
                            required
                        />
                    </e-field>
                </section>

                <footer class="modal-card-foot">
                    <div class="buttons">
                        <e-button @click="closeModal">Cancel</e-button>
                        <e-button :loading="submittingTicket" type="is-primary" @click="submitProductionTicket">
                            Send
                        </e-button>
                    </div>
                </footer>
            </div>
        </e-modal>
    </div>
</template>

<script>
import get from 'lodash/get.js';
import isPlainObject from 'lodash/isPlainObject.js';
import { mapState } from 'vuex';

import Editor from '@/components/editor/Editor.vue';
import { initialState } from '@/Helpers.js';
import axios from '@/plugins/axios.js';
import VisualExceptions from '@/render-exception.js';

export default {
    name: 'GlobalErrorHandler',

    components: { RichTextEditor: Editor },

    data: () => ({
        requestTab: 3,
        modalActive: false,
        activeErrorIndex: 0,
        openedSnackbars: [],
        submittingTicket: false,

        productionTicketForm: {
            description: '',
            priority: 2,
        },
    }),

    computed: {
        ...mapState(['globalErrors']),
        ...mapState('Auth', { authUser: state => state.user }),

        errors() {
            return this.globalErrors;
        },

        /**
         * @returns {ErrorPackage|null}
         */
        currentError() {
            return this.errors[this.activeErrorIndex] || null;
        },

        isDev() {
            return initialState('Debug');
        },
    },

    watch: {
        /**
         * Whenever the errors array changes, we'll find newly added errors
         * and show a snackbar for each new one.
         */
        errors: {
            handler(newVal, oldVal) {
                const oldLength = oldVal ? oldVal.length : 0;
                const newLength = newVal.length;

                // Show a snack for each newly added error
                if (newLength > oldLength) {
                    for (let i = oldLength; i < newLength; i++) {
                        if (newVal[i].shouldShowSnackbar) {
                            this.showSnackbarForError(newVal[i]);
                        }
                    }
                }
            },
            deep: true,
            immediate: true,
        },
    },

    methods: {
        /**
         * Show a Buefy snackbar for the given error item.
         */
        showSnackbarForError(errorItem) {
            let actionText = 'Dismiss';

            // In dev, we want "Details" to open the dev modal.
            // In production, encourage the user to "Report" the issue.
            if (this.isDev) {
                actionText = 'Details';
            } else {
                actionText = 'Report';
            }

            // Ensure we have a message to display
            let message = errorItem.userMessage;
            if (!message || message.trim() === '') {
                message = 'An error occurred. Please try again.';
            }

            const snack = this.$snackbar.open({
                message,
                type: 'is-danger',
                indefinite: true,
                position: 'is-bottom-left',
                queue: !this.isDev,
                actionText,
                cancelText: 'Close',
                onAction: () => this.openModal(errorItem.id),
                group: 'global-errors', // Use a group for easier management
            });

            // Keep track of the snackbar so we can close it later
            this.openedSnackbars.push(snack);
        },

        /**
         * Open the modal (dev or production).
         */
        openModal(id) {
            const index = this.errors.findIndex(err => err.id === id);

            if (index !== -1) {
                this.activeErrorIndex = index;
                this.modalActive = true;
            }
        },

        closeModal() {
            this.modalActive = false;
        },

        clearAllErrors() {
            this.$store.commit('CLEAR_GLOBAL_ERRORS');

            // Close all open snackbars using the group
            this.$snackbar.removeGroup('global-errors');

            // Also close individually tracked snackbars as fallback (for Buefy mode)
            this.openedSnackbars.forEach(snack => snack.close());
            this.openedSnackbars = [];

            this.closeModal();
        },

        dismissError(id) {
            const index = this.errors.findIndex(err => err.id === id);

            if (index !== -1) {
                this.$store.commit('DISMISS_GLOBAL_ERROR', index);
            }
        },

        nextError() {
            if (this.activeErrorIndex < this.errors.length - 1) {
                this.activeErrorIndex++;
            }
        },

        prevError() {
            if (this.activeErrorIndex > 0) {
                this.activeErrorIndex--;
            }
        },

        isAxiosError(error) {
            return error?.config && error?.request;
        },

        formatted(key) {
            const err = get(this.currentError.err, key);

            if (!err) return 'N/A';

            // If it's already a string, attempt to parse it
            if (typeof err === 'string') {
                try {
                    const parsed = JSON.parse(err);
                    return JSON.stringify(parsed, null, 2);
                } catch (e) {
                    // Not valid JSON, just return it as-is
                    return err;
                }
            }

            // If it's an object, pretty-print
            if (isPlainObject(err) || Array.isArray(err)) {
                return JSON.stringify(err, null, 2);
            }

            // Fallback
            return String(err);
        },

        async submitProductionTicket() {
            try {
                this.submittingTicket = true;

                const errorInfo = {
                    message: this.currentError?.err?.message || 'No message available',
                    info: this.currentError?.info || 'No additional info',
                    date: this.currentError?.date || new Date().toISOString(),
                    diagnostics: this.currentError?.err?.stack || 'No stack trace available',
                };

                const payload = {
                    description: this.productionTicketForm.description,
                    sentryId: this.currentError?.sentryId,
                    errorInfo, // Pass the extracted errorInfo object
                };

                await axios.post('/api/submit-ticket', payload);

                this.submittingTicket = false;

                this.$snackbar.open({
                    message: 'Your ticket was submitted successfully.',
                    type: 'is-success',
                    duration: 5000,
                });

                this.productionTicketForm.description = '';
                this.closeModal();

                this.dismissError(this.currentError.id);
            } catch (err) {
                this.$snackbar.open({
                    message: 'Failed to submit the ticket. Please try again later.',
                    type: 'is-danger',
                    duration: 5000,
                });
            }
        },

        openVisualException() {
            VisualExceptions.retrieveLastError(false);
        },
    },
};
</script>

<style lang="scss" scoped>
.global-error-handler {
    z-index: 10;
}

</style>
